import { useCallback, useEffect, useRef, useState } from 'react';
import { useSttChunkRecorder } from '@/hooks/useSttChunkRecorder';
import { useWebSpeechTranscript } from './useWebSpeechTranscript';
import audioFallback from '@/assets/interview/audio/audio_fallback.wav';

type MicMode = "listening" | "processing" | "ai_speaking" | "empty_audio" | "idle";
type AiState = "PROCESSING" | "LISTENING" | "SPEAKING" | "AI_RETRYING" | "CODING_EXERCISE" | "IMPROVING_SPEECH" | "AWAIT_SUBMIT";

type UseVoiceInteractionOptions = {
  interviewId?: string;
  timeLabel: string;
  timestampHHMMSS: string;
  sendUserMessage: (params: { message: string; timestamp: string }) => Promise<void>;
  onUserMessage: (message: string, displayTime: string) => void;
  isInterviewEnding?: boolean;
};

export function useVoiceInteraction(options: UseVoiceInteractionOptions) {
  const { interviewId, timeLabel, timestampHHMMSS, sendUserMessage, onUserMessage, isInterviewEnding } = options;

  const [micMode, setMicMode] = useState<MicMode>("ai_speaking");
  const [aiInterviewState, setAiInterviewState] = useState<AiState>("SPEAKING");
  const [analyzeText, setAnalyzeText] = useState<string>("");
  const [noticeText, setNoticeText] = useState<string | null>(null);
  const awaitingFirstChunkRef = useRef<boolean>(false);
  const ttsStartedRef = useRef<boolean>(false);
  const prevSpeakingRef = useRef<boolean>(false);
  const finalTranscriptRef = useRef<string | null>(null);
  const lastChunkTextRef = useRef<string | null>(null);
  const fallbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const fallbackTimeoutRef = useRef<number | null>(null);

  const {
    transcript,
    start: startWebSpeech,
    stop: stopWebSpeech,
    reset: resetWebSpeech,
    hardReset: hardResetWebSpeech,
  } = useWebSpeechTranscript();

  const {
    startListening: startSttListening,
    doneListening,
    lastChunkText,
    finalTranscript,
    error: sttError,
  } = useSttChunkRecorder({ interviewId: interviewId || '' });

  useEffect(() => {
    finalTranscriptRef.current = finalTranscript;
  }, [finalTranscript]);

  useEffect(() => {
    lastChunkTextRef.current = lastChunkText;
  }, [lastChunkText]);

  useEffect(() => {
    setNoticeText(sttError);
  }, [sttError]);

  const startListeningCycle = useCallback(() => {
    setMicMode("listening");
    setAiInterviewState("LISTENING");
    setAnalyzeText("");
    setNoticeText(null);
    awaitingFirstChunkRef.current = false;
    finalTranscriptRef.current = null;
    lastChunkTextRef.current = null;
    resetWebSpeech();
    void startSttListening();
    startWebSpeech();
  }, [resetWebSpeech, startSttListening, startWebSpeech]);

  useEffect(() => {
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      if (fallbackAudioRef.current) {
        fallbackAudioRef.current.pause();
        fallbackAudioRef.current.src = "";
      }
    };
  }, []);

  const playFallbackAndReset = useCallback(() => {
    // Lazily create audio to keep user gesture chain intact
    if (!fallbackAudioRef.current) {
      fallbackAudioRef.current = new Audio(audioFallback);
    }
    const audioEl = fallbackAudioRef.current;
    audioEl.currentTime = 0;

    const resetToListening = () => {
      audioEl.onended = null;
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
      startListeningCycle();
    };

    // Safety timeout so we never get stuck in empty_audio
    fallbackTimeoutRef.current = window.setTimeout(resetToListening, 3000);

    audioEl.onended = resetToListening;
    audioEl.play().catch(() => {
      // If playback fails (e.g., blocked), still resume listening
      resetToListening();
    });
  }, [startListeningCycle]);

  const handleDone = useCallback(async () => {
    setMicMode("processing");
    setAiInterviewState("IMPROVING_SPEECH");
    setAnalyzeText(transcript);

    stopWebSpeech();
    const sttFinal = await doneListening();

    const combinedTranscript =
      (sttFinal ?? "").trim() ||
      (finalTranscriptRef.current ?? "").trim() ||
      (transcript ?? "").trim() ||
      (lastChunkTextRef.current ?? "").trim();

    if (!combinedTranscript) {
      setMicMode("empty_audio");
      setAiInterviewState("PROCESSING");
      playFallbackAndReset();
      return;
    }

    awaitingFirstChunkRef.current = true;
    onUserMessage(combinedTranscript, timeLabel);

    try {
      await sendUserMessage({
        message: combinedTranscript,
        timestamp: timestampHHMMSS,
      });
    } catch (error) {
      setNoticeText("Failed to send message. Please try again.");
    }
    hardResetWebSpeech();
  }, [doneListening, onUserMessage, sendUserMessage, stopWebSpeech, timeLabel, timestampHHMMSS, transcript, hardResetWebSpeech]);

  const handleTtsSpeakingChange = useCallback(
    (isSpeaking: boolean) => {
      const wasSpeaking = prevSpeakingRef.current;

      if (isSpeaking) {
        ttsStartedRef.current = true;
        setMicMode("ai_speaking");
        setAiInterviewState("SPEAKING");
        stopWebSpeech();
        prevSpeakingRef.current = true;
        return;
      }

      // Don't start listening if interview is ending; move to processing instead
      if (isInterviewEnding) {
        if (!isSpeaking) {
          setMicMode("idle");
          setAiInterviewState("PROCESSING");
          stopWebSpeech();
        }
        prevSpeakingRef.current = isSpeaking;
        return;
      }

      if (wasSpeaking && ttsStartedRef.current && micMode === "ai_speaking") {
        startListeningCycle();
      }
      prevSpeakingRef.current = isSpeaking;
    },
    [micMode, startListeningCycle, stopWebSpeech, isInterviewEnding],
  );

  // Ensure end-of-interview flow forces idle mic and clears analysis text
  useEffect(() => {
    if (!isInterviewEnding) return;
    setMicMode("idle");
    setAiInterviewState("PROCESSING");
    setAnalyzeText("");
    awaitingFirstChunkRef.current = false;
    stopWebSpeech();
  }, [isInterviewEnding, stopWebSpeech]);

  const onFirstChunk = useCallback(() => {
    if (!awaitingFirstChunkRef.current) return;
    setAiInterviewState("PROCESSING");
    awaitingFirstChunkRef.current = false;
  }, []);

  return {
    micMode,
    aiInterviewState,
    analyzeText,
    liveTranscript: transcript,
    noticeText,
    handleDone,
    handleTtsSpeakingChange,
    onFirstChunk,
  };
}
